import { X } from 'lucide-react';
import type { PosPriceMap } from '../../lib/api';
import { lineCap, type CartLine as Line } from '../../lib/cart-store';
import { productPrice, variantPrice } from '../../lib/pos-pricing';
import { PriceTag } from './price-tag';
import { ProductImage } from './product-image';
import { QtyStepper } from './qty-stepper';

/**
 * One line of the cart: photo, name, unit price, a typeable quantity and the
 * line total. The price is read at render (never stored on the line), so
 * switching the buyer re-prices lines already in the cart.
 */
export function CartLine({
  line,
  priceMap,
  showPhoto,
  onStep,
  onSet,
  onRemove,
}: {
  line: Line;
  priceMap: PosPriceMap | null;
  /** Off for a catalog with no photos at all (see the catalog's dense tiles). */
  showPhoto: boolean;
  onStep: (delta: number) => void;
  onSet: (n: number) => void;
  onRemove: () => void;
}) {
  const unit = line.variant
    ? variantPrice(line.variant, priceMap)
    : productPrice(line.product, priceMap);
  return (
    <li className="flex gap-3 py-3">
      {showPhoto && (
        <ProductImage
          product={line.product}
          className="h-[86px] w-[121px] shrink-0 rounded-[5px]"
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-start gap-1">
          <div className="min-w-0 flex-1">
            <div className="line-clamp-2 text-base font-semibold capitalize leading-tight">
              {line.product.name}
            </div>
            {line.variant && (
              <div className="truncate text-sm text-muted-foreground">{line.variant.name}</div>
            )}
          </div>
          <button
            type="button"
            aria-label={`Remove ${line.product.name}`}
            onClick={onRemove}
            className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center text-muted-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="text-sm">
          <PriceTag base={unit.base} now={unit.now} />
        </div>
        <div className="mt-auto flex items-center justify-between gap-2">
          <QtyStepper qty={line.qty} cap={lineCap(line)} onStep={onStep} onSet={onSet} />
          <span className="text-base font-bold tabular-nums text-primary">
            ₱{(unit.now * line.qty).toFixed(2)}
          </span>
        </div>
      </div>
    </li>
  );
}
